import {useCallback, useContext, useState} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import type {Action, UnknownAction} from 'redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {
  selectIsEditingDesign,
  setIsEditingDesign,
} from '~/CreateFabricInvitation/reducers/preview/reducer';
import {scrollElementIntoViewById} from '~/CreateFabricInvitation/utils/misc';
import {webViewPendingUpgradeToPremium} from '~/CreateFabricInvitation/webview/webview';
import {FabricInvitationContext} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {
  selectIsPremium,
  selectIsPremiumTemplate,
  selectIsProOrPartyPass,
} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {webViewModalShowing} from '~/common/fabric/hooks';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {eventInEditMode} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {toggleOption} from '~/common/fabric/reducers/giftingOptions/actions';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectGiftingOptions,
  selectGiftOptionsCountInComponent,
  selectHostPhotoGalleryEnabled,
  selectPhotosEnabled,
  selectShowGifting,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {SET_UPSELL_CONFIRMED} from '~/common/fabric/reducers/modals';
import {UPSELL_MODES} from '~/common/fabric/UpsellModal/UpsellModal';
import {EventFeature} from '~/common/types/event';
import {TOGGLE_HOST_GALLERY_EDITOR_OPEN} from '~/ViewInvitation/reducers/constants';
import {initPremiumDesign} from '~/ViewInvitation/reducers/premiumDesign';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: too many lines!
export const useFeatureControls = () => {
  const {
    featureToggling,
    setFeatureToggling,
    showUpsellModal,
    setShowUpsellModal,
    upsellMode,
    setUpsellMode,
    inAdsToggle,
    setInAdsToggle,
  } = useContext(FabricInvitationContext);
  const current = useCurrentRoute();
  const store = useStore();
  const isDesktop = useIsLargishAndUp();
  const [showCharityController, setShowCharityController] = useState(false);
  const [showCrowdfundController, setShowCrowdfundController] = useState(false);

  const inEditMode = useSelector(eventInEditMode);
  const isPremium = useSelector(selectIsPremium);
  const showGifting = useSelector(selectShowGifting);
  const giftingOptions = useSelector(selectGiftingOptions, shallowEqual);
  const isPhotosEnabled: boolean = useSelector(selectPhotosEnabled);
  const isHostPhotoGalleryEnabled: boolean = useSelector(selectHostPhotoGalleryEnabled);
  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const giftOptionsCount = useSelector(selectGiftOptionsCountInComponent);
  const isEditingDesign = useSelector(selectIsEditingDesign);

  // [] syntax in case we add more upsells with these features
  const upsellHasBannerImg = Boolean(
    featureToggling && [EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );
  const upsellUsesAccordion = Boolean(
    featureToggling && [EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );
  const photoFeatureToggled = Boolean(
    featureToggling &&
      [EventFeature.PHOTO_SHARING, EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );

  const setPhotoSharing = useCallback(
    (enabled: boolean) => {
      store.dispatch(toggleOption(giftingOptions, 'event_option_enable_photo_sharing', enabled));
    },
    [store, giftingOptions]
  );

  const enableHostPhotoGallery = useCallback(
    (enabled: boolean) => {
      store.dispatch(
        toggleOption(giftingOptions, 'event_option_enable_host_photo_gallery', enabled)
      );
    },
    [store, giftingOptions]
  );

  const removeGifting = useCallback(async () => {
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        showGifting: false,
        options: {
          ...giftingOptions.options,
          // biome-ignore lint/style/useNamingConvention: existing error
          event_option_show_gifting: false,
        },
      },
    });
  }, [store, giftingOptions]);

  const toggleUpsellModal = useCallback(() => {
    setShowUpsellModal((prev) => {
      if (!prev) {
        webViewModalShowing('upsell');
      } else {
        setUpsellMode(undefined);
      }
      return !prev;
    });
  }, [setShowUpsellModal, setUpsellMode]);

  const onConfirmOrDeny = useCallback(async () => {
    if (!inAdsToggle && !photoFeatureToggled) {
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: true},
      });
    }

    store.dispatch(saveEventThunk(!inEditMode) as unknown as Action);
    webViewPendingUpgradeToPremium(true);
    if (!inAdsToggle && !photoFeatureToggled) {
      window.dataLayer.push({
        event: 'fabric_create_upgrade_ads_toggle',
      });
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: false},
      });
    }

    setInAdsToggle(false);
  }, [inAdsToggle, photoFeatureToggled, store, inEditMode, setInAdsToggle]);

  const upsellConfirm = useCallback(async () => {
    store.dispatch({
      type: SET_UPSELL_CONFIRMED,
      payload: true,
    });
    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: true, hideUpsell: true},
    });
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        upgraded: true,
      },
    });

    switch (featureToggling) {
      case EventFeature.PHOTO_SHARING:
      case EventFeature.ADS:
        setPhotoSharing(true);
        break;
      case EventFeature.HOST_PHOTO_GALLERY:
        enableHostPhotoGallery(true);
        if (isDesktop) {
          store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
        }
        break;
      case EventFeature.GIFT_IDEAS:
        await removeGifting();
        break;
      case EventFeature.PREMIUM_DESIGN_TOOLS:
        store.dispatch(setIsEditingDesign(false));
        break;
      default:
        break;
    }
    toggleUpsellModal();

    await onConfirmOrDeny();

    setInAdsToggle(false);
  }, [
    store,
    enableHostPhotoGallery,
    featureToggling,
    onConfirmOrDeny,
    toggleUpsellModal,
    setPhotoSharing,
    removeGifting,
    setInAdsToggle,
    isDesktop,
  ]);

  const onToggleUpsellModal = useCallback(async () => {
    if (featureToggling === EventFeature.ADS) {
      toggleUpsellModal();
      if (!inAdsToggle) await onConfirmOrDeny();
      setInAdsToggle(!inAdsToggle);
    } else if (featureToggling === EventFeature.PHOTO_SHARING) {
      if (isPremium) {
        setPhotoSharing(true);
      }
      toggleUpsellModal();
    } else if (featureToggling === EventFeature.HOST_PHOTO_GALLERY) {
      if (isPremium) {
        enableHostPhotoGallery(true);
      }
      toggleUpsellModal();
    }
    setFeatureToggling(null);
  }, [
    featureToggling,
    inAdsToggle,
    isPremium,
    onConfirmOrDeny,
    toggleUpsellModal,
    setPhotoSharing,
    enableHostPhotoGallery,
    setInAdsToggle,
    setFeatureToggling,
  ]);

  const onCloseUpsellOverlay = useCallback(async () => {
    if (featureToggling === EventFeature.ADS) {
      if (!inAdsToggle) {
        store.dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {savingStep: true},
        });
      }

      webViewPendingUpgradeToPremium(false);

      if (!inAdsToggle) {
        await navigateToRoute(current.nextRoute, store);
      }

      setInAdsToggle(false);
    } else if (featureToggling === EventFeature.PREMIUM_DESIGN_TOOLS && isEditingDesign) {
      // Reset premium design choices
      if (window.evite_event) {
        store.dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
      }
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: false,
        },
      });
      // Re-sync latest state of card design
      await store.dispatch(saveEventThunk(false) as unknown as UnknownAction);
      // Close the design editor
      store.dispatch(setIsEditingDesign(false));
    } else if (photoFeatureToggled) {
      webViewPendingUpgradeToPremium(false);
    }
    setFeatureToggling(null);
    toggleUpsellModal();
  }, [
    current?.nextRoute,
    featureToggling,
    photoFeatureToggled,
    inAdsToggle,
    store,
    toggleUpsellModal,
    setInAdsToggle,
    setFeatureToggling,
    isEditingDesign,
  ]);

  const addGifting = useCallback(async () => {
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        showGifting: true,
        options: {
          ...giftingOptions.options,
          // biome-ignore lint/style/useNamingConvention: existing error
          event_option_show_gifting: true,
        },
      },
    });
  }, [store, giftingOptions]);

  const removeAds = useCallback(() => {
    setInAdsToggle(true);
    setFeatureToggling(EventFeature.ADS);
    setUpsellMode(UPSELL_MODES.REMOVE_ADS);
    toggleUpsellModal();
  }, [toggleUpsellModal, setFeatureToggling, setUpsellMode, setInAdsToggle]);

  const unlockDesignTools = useCallback(() => {
    setFeatureToggling(EventFeature.PREMIUM_DESIGN_TOOLS);
    setUpsellMode(UPSELL_MODES.UNLOCK_DESIGN_TOOLS);
    toggleUpsellModal();
  }, [toggleUpsellModal, setFeatureToggling, setUpsellMode]);

  const handlePremiumFeatureToggle = useCallback(
    (featureToToggle: EventFeature) => {
      switch (featureToToggle) {
        case EventFeature.PHOTO_SHARING: {
          // If turning off, scroll down to it before removing
          if (isPhotosEnabled) scrollElementIntoViewById('module-photo', 'center', 0);
          setPhotoSharing(!isPhotosEnabled);
          setFeatureToggling(EventFeature.PHOTO_SHARING);
          // If just turned on, scroll down to it after turning it on
          if (!isPhotosEnabled) scrollElementIntoViewById('module-photo');
          break;
        }
        case EventFeature.HOST_PHOTO_GALLERY: {
          if (!isHostPhotoGalleryEnabled || !isDesktop) {
            // Host Photo Gallery is off, or we're on MW which only has on/off for the feature
            if (isPremium || isHostPhotoGalleryEnabled) {
              // If premium invitation or host gallery is on, toggle the feature
              enableHostPhotoGallery(!isHostPhotoGalleryEnabled);
              if (isDesktop && !isHostPhotoGalleryEnabled) {
                // If on desktop and host gallery is off, also open the editor
                store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
              }
            } else {
              // Free invitation; show Premium upsell
              setFeatureToggling(EventFeature.HOST_PHOTO_GALLERY);
              setUpsellMode(UPSELL_MODES.HOST_PHOTO_GALLERY);
              toggleUpsellModal();
            }
          } else if (isDesktop) {
            // Host Photo Gallery is on and we're on desktop, so open the editor
            store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
          }
          break;
        }
        case EventFeature.GIFT_IDEAS: {
          if (showGifting) {
            const shouldShowUpsellModal =
              !isPremiumTemplate && !isPremium && !isProOrPartyPass && giftOptionsCount === 1;
            if (shouldShowUpsellModal) {
              setFeatureToggling(EventFeature.GIFT_IDEAS);
              setUpsellMode(UPSELL_MODES.GIFTING);
              toggleUpsellModal();
            } else {
              removeGifting();
            }
          } else {
            addGifting();
          }
          break;
        }
        default:
          break;
      }
    },
    [
      enableHostPhotoGallery,
      isHostPhotoGalleryEnabled,
      isDesktop,
      store,
      isPremiumTemplate,
      isPremium,
      isProOrPartyPass,
      showGifting,
      giftOptionsCount,
      isPhotosEnabled,
      setPhotoSharing,
      toggleUpsellModal,
      removeGifting,
      addGifting,
      setFeatureToggling,
      setUpsellMode,
    ]
  );

  const handleFeatureClick = useCallback(
    (feature: EventFeature) => {
      switch (feature) {
        case EventFeature.HOST_PHOTO_GALLERY: {
          handlePremiumFeatureToggle(EventFeature.HOST_PHOTO_GALLERY);
          break;
        }
        case EventFeature.PHOTO_SHARING: {
          handlePremiumFeatureToggle(EventFeature.PHOTO_SHARING);
          break;
        }
        case EventFeature.CHARITY: {
          setShowCharityController((prev) => !prev);
          break;
        }
        case EventFeature.CROWDFUNDING: {
          setShowCrowdfundController((prev) => !prev);
          break;
        }
        default:
          // Logic is already handled by the Component attribute for the feature
          break;
      }
    },
    [handlePremiumFeatureToggle]
  );

  return {
    featureToggling,
    setFeatureToggling,
    showUpsellModal,
    setShowUpsellModal,
    upsellMode,
    setUpsellMode,
    upsellHasBannerImg,
    upsellUsesAccordion,
    isPhotosEnabled,
    isHostPhotoGalleryEnabled,
    isPremium,
    onToggleUpsellModal,
    onCloseUpsellOverlay,
    upsellConfirm,
    setPhotoSharing,
    enableHostPhotoGallery,
    handlePremiumFeatureToggle,
    removeAds,
    unlockDesignTools,
    handleFeatureClick,
    showCharityController,
    showCrowdfundController,
  };
};
